// AlertModal.js
// A friendly modal for displaying pipeline results

import {useEffect} from 'react';

export const AlertModal = ({ isOpen, onClose, data }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 8000); // Auto-close after 8 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isDag = data?.is_dag;
  const numNodes = data?.num_nodes || 0;
  const numEdges = data?.num_edges || 0;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] animate-fadeIn">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-slideUp">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Icon */}
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
          isDag ? 'bg-green-100' : 'bg-orange-100'
        }`}>
          {isDag ? (
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          {isDag ? 'Pipeline Looks Good!' : 'Oops! Cycle Detected'}
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6">
          {isDag 
            ? 'Your pipeline is valid and ready to execute!' 
            : 'Your pipeline contains cycles. This could cause infinite loops.'}
        </p>

        {/* Stats */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Nodes:</span>
            <span className="text-lg font-semibold text-gray-900">{numNodes}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Connections:</span>
            <span className="text-lg font-semibold text-gray-900">{numEdges}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">DAG Status:</span>
            <span className={`text-lg font-semibold ${isDag ? 'text-green-600' : 'text-orange-600'}`}>
              {isDag ? 'Valid ✓' : 'Invalid ✗'}
            </span>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={onClose}
          className={`w-full py-3 rounded-lg font-semibold transition-colors ${
            isDag 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-orange-600 hover:bg-orange-700 text-white'
          }`}
        >
          Got it!
        </button>

        {/* Auto-close indicator */}
        <p className="text-xs text-gray-400 text-center mt-3">
          Auto-closes in 8 seconds
        </p>
      </div>
    </div>
  );
};
